import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, CheckCircle, XCircle, Edit3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import Layout from '../components/layout/Layout';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get auth token from storage
const getAuthToken = () => {
  try {
    return localStorage.getItem('fuse_auth_token') || sessionStorage.getItem('fuse_auth_token');
  } catch (e) {
    return null;
  }
};

const writingPrompts = [
  { id: 1, title: 'Apresentação', prompt: 'Escreva uma apresentação sobre você em inglês (nome, de onde é, onde trabalha).', example: 'My name is... I am from... I work at...' },
  { id: 2, title: 'Pedindo Ajuda', prompt: 'Escreva uma mensagem pedindo ajuda a um colega de trabalho.', example: 'Excuse me, can you help me with...' },
  { id: 3, title: 'Marcando Consulta', prompt: 'Escreva uma mensagem para marcar uma consulta médica.', example: 'I would like to make an appointment...' },
  { id: 4, title: 'Descrevendo Sintomas', prompt: 'Descreva seus sintomas para um médico em inglês.', example: 'I have been feeling... since...' },
  { id: 5, title: 'Pedido no Restaurante', prompt: 'Faça um pedido em um restaurante em inglês.', example: 'I would like to order...' },
  { id: 6, title: 'E-mail de Trabalho', prompt: 'Escreva um e-mail curto para um colega de trabalho.', example: 'Dear... I am writing to...' },
];

const WritingPage = () => {
  const navigate = useNavigate();
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [text, setText] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const checkWriting = async () => {
    if (!text.trim() || isLoading) return;

    setIsLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.post(`${API_URL}/api/writing/check`, {
        text: text.trim(),
        context: selectedPrompt?.title
      }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      setFeedback(response.data);
    } catch (error) {
      console.error('Writing check error:', error);
      // Mock feedback if API fails
      setFeedback({
        original: text,
        corrected: text,
        feedback: 'Não foi possível verificar o texto. Tente novamente mais tarde.',
        score: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetExercise = () => {
    setText('');
    setFeedback(null);
  };

  const selectNewPrompt = () => {
    setSelectedPrompt(null);
    setText('');
    setFeedback(null);
  };

  // Prompt selection view
  if (!selectedPrompt) {
    return (
      <Layout showBack title="Exercícios de Escrita">
        <div className="px-4 py-6 space-y-6">
          <div className="space-y-1">
            <h1 className="font-display text-2xl font-bold">Exercícios de Escrita</h1>
            <p className="text-muted-foreground">
              Pratique escrever em inglês e receba correções
            </p>
          </div>

          <div className="space-y-3">
            {writingPrompts.map((prompt) => (
              <button
                key={prompt.id}
                onClick={() => setSelectedPrompt(prompt)}
                className="card-lesson w-full text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Edit3 className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{prompt.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {prompt.prompt}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  // Writing exercise view
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={selectNewPrompt}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-semibold truncate">{selectedPrompt.title}</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Prompt */}
          <div className="card-elevated p-4 space-y-2">
            <h2 className="font-semibold text-sm text-primary">Exercício:</h2>
            <p className="text-foreground">{selectedPrompt.prompt}</p>
            <p className="text-sm text-muted-foreground italic">
              Exemplo: "{selectedPrompt.example}"
            </p>
          </div>

          {/* Writing Area */}
          {!feedback ? (
            <div className="space-y-4">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Escreva seu texto em inglês aqui..."
                className="min-h-[200px] resize-none"
                disabled={isLoading}
              />
              
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {text.length} caracteres
                </p>
                <Button
                  onClick={checkWriting}
                  disabled={!text.trim() || isLoading}
                  className="btn-primary-gradient"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Verificar
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 animate-fade-in">
              {/* Score */}
              <div className={cn(
                'p-4 rounded-xl text-center',
                feedback.score >= 80 ? 'bg-success/10' : 
                feedback.score >= 50 ? 'bg-warning/10' : 'bg-destructive/10'
              )}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {feedback.score >= 80 ? (
                    <CheckCircle className="w-6 h-6 text-success" />
                  ) : (
                    <XCircle className="w-6 h-6 text-warning" />
                  )}
                  <span className="text-2xl font-bold">
                    {feedback.score}/100
                  </span>
                </div>
                <Progress 
                  value={feedback.score} 
                  className={cn(
                    'h-2',
                    feedback.score >= 80 ? '[&>div]:bg-success' : 
                    feedback.score >= 50 ? '[&>div]:bg-warning' : '[&>div]:bg-destructive'
                  )}
                />
              </div>

              {/* Original Text */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                  Seu texto:
                </h3>
                <div className="p-3 rounded-lg bg-muted text-sm">
                  {feedback.original}
                </div>
              </div>

              {/* Corrected Text */}
              {feedback.corrected !== feedback.original && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm flex items-center gap-2 text-success">
                    <CheckCircle className="w-4 h-4" />
                    Texto corrigido:
                  </h3>
                  <div className="p-3 rounded-lg bg-success/10 text-sm border border-success/20">
                    {feedback.corrected}
                  </div>
                </div>
              )}

              {/* Feedback */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm flex items-center gap-2 text-primary">
                  <Edit3 className="w-4 h-4" />
                  Feedback:
                </h3>
                <div className="p-3 rounded-lg bg-primary/5 text-sm border border-primary/20">
                  {feedback.feedback}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={selectNewPrompt}
                >
                  Outro exercício
                </Button>
                <Button
                  className="flex-1 btn-primary-gradient"
                  onClick={resetExercise}
                >
                  Tentar novamente
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default WritingPage;
